//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { FormEnums, StyleEnums } from "models/enums";
import { AutoCompleteExtraProps, GenericFormSection } from "models/propTypes/genericFormProps";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { BusinessUnitFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { BusinessUnitFormViewProps } from "models/propTypes/businessUnitPropTypes";
import { MessageBubbleDetails } from "models/propTypes/messagePropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#region components
import { useBusinessUnitService, useCompanyService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

function useGeneralInfoFields(props: BusinessUnitFormViewProps): GenericFormSection<BusinessUnitFormTypes.Create>[] {
  const companyService = useCompanyService();
  const businessUnitService = useBusinessUnitService();
  const { isCreate } = props;
  const dimensionMaps = useSelector((store: AppState) => {
    return store.common.dimensions.allDimensions;
  });

  const checkBusinessUnitNameDimensionAlreadyExists = async (values: BusinessUnitFormTypes.Create) => {
    if (!isCreate) {
      return;
    }

    const dimensions = dimensionMaps?.find(
      (item) => item.key.type === WintSuperkollAPIModel.DimensionCompatibilityType.BusinessUnit
    )?.value;
    const existingNameDimensions =
      dimensions?.filter(
        (c) => c.id !== undefined && c.name?.toLowerCase() === values.businessUnitName?.toLowerCase()
      ) ?? [];
    const result = existingNameDimensions.length > 0;

    const message: MessageBubbleDetails | undefined = result
      ? {
          type: StyleEnums.DisplayType.Warning,
          message: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.BusinessUnit.DimensionAlreadyExists),
        }
      : undefined;

    return message;
  };

  return [
    {
      label: undefined,
      type: FormEnums.FormSectionType.FIELDGROUPSECTION,
      icon: IconDefinitionConstants.WMoney,
      innerFields: {
        businessUnitName: {
          controlLabel: TranslationKeys.BusinessUnit.BusinessUnitName,
          label: TranslationKeys.Common.Name,
          type: FormEnums.FormFieldType.TEXT,
          descriptionText: TranslationKeys.BusinessUnit.BusinessUnitNameDescription,
          customMessageHandler: checkBusinessUnitNameDimensionAlreadyExists,
        },
        managerName: {
          controlLabel: TranslationKeys.BusinessUnit.ResponsibleManager,
          label: TranslationKeys.Common.Responsible,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          descriptionText: TranslationKeys.BusinessUnit.ResponsibleManagerDescription,
          icon: IconDefinitionConstants.WUser,
          disabled: businessUnitService.hasRestrictedEditPermissions(),
          extraProps: {
            items: (searchStr: string) => companyService.SearchPersonsAutoComplete(searchStr, false),
            referenceFieldName: FormFieldConstants.BusinessUnit.Create.ManagerId,
          } as AutoCompleteExtraProps<BusinessUnitFormTypes.Create>,
        },
      },
    },
    {
      label: TranslationKeys.BusinessUnit.ManagerPermissions,
      type: FormEnums.FormSectionType.FIELDGROUPSECTION,
      icon: IconDefinitionConstants.WLock,
      innerFields: {
        managerHasSalaryRights: {
          label: TranslationKeys.BusinessUnit.SalaryResponsibility,
          type: FormEnums.FormFieldType.TOGGLE,
          descriptionText: TranslationKeys.BusinessUnit.SalaryResponsibilityDescription,
        },
      },
      hidden: !props.isAdmin,
    },
  ];
}

export default useGeneralInfoFields;
